import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Paper, Stack, Typography } from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';
import { PublicInvoiceRequestError, publicInvoicePaymentService, type PublicInvoiceView, type PublicPaymentIntent } from '../services/publicInvoicePaymentService';

const tokenPattern = /^[A-Za-z0-9_-]{43}$/;
const money = (value: string) => `BBD ${value}`;
const contact = <a href="mailto:info@made.bb">info@made.bb</a>;

function safeProviderUrl(intent: PublicPaymentIntent): string | null {
  try {
    const parsed = new URL(intent.provider_url);
    if (parsed.protocol !== 'https:' || !parsed.hostname || parsed.username || parsed.password || parsed.hash) return null;
    if (!Number.isFinite(Date.parse(intent.expires_at)) || Date.parse(intent.expires_at) <= Date.now()) return null;
    return parsed.toString();
  } catch { return null; }
}

export const PublicInvoicePaymentPage: React.FC = () => {
  const { token = '' } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  // Presentation hints only. The server's signed webhook is the sole source of Paid.
  const returned = params.get('payment_return') === '1';
  const failedReturn = params.get('payment_failed') === '1';
  const [invoice, setInvoice] = useState<PublicInvoiceView | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [intentError, setIntentError] = useState('');
  const [intent, setIntent] = useState<PublicPaymentIntent | null>(null);
  const [mockCompleting, setMockCompleting] = useState(false);

  const refresh = useCallback(async (initial = false) => {
    if (!tokenPattern.test(token)) { setInvoice(null); setLoadError('This invoice link is unavailable or expired.'); setLoading(false); return; }
    if (initial) setLoading(true); else setRefreshing(true);
    try {
      const next = await publicInvoicePaymentService.get(token);
      if (next.currency !== 'BBD') throw new Error('Card payment is unavailable for this invoice.');
      setInvoice(next); setLoadError('');
    } catch (reason) {
      // An old payable view must not remain actionable after link revocation or a failed refresh.
      setInvoice(null);
      setLoadError(reason instanceof PublicInvoiceRequestError && reason.status === 404
        ? 'This invoice link is unavailable or expired.'
        : reason instanceof Error ? reason.message : 'Could not load the invoice.');
    } finally { setLoading(false); setRefreshing(false); }
  }, [token]);

  useEffect(() => { setInvoice(null); void refresh(true); }, [refresh]);

  const createIntent = async () => {
    if (!invoice || submitting || !invoice.can_pay || invoice.status !== 'payment_due') return;
    setSubmitting(true); setIntentError(''); setIntent(null);
    try {
      const created = await publicInvoicePaymentService.createIntent(token);
      if (created.currency !== 'BBD' || created.amount !== invoice.amount_due_now || !safeProviderUrl(created)) {
        setIntentError('The payment amount or link changed. Refresh the invoice before continuing.');
        await refresh(false);
        return;
      }
      setIntent(created); setConfirmOpen(false);
    } catch (reason) {
      setIntentError(reason instanceof Error ? reason.message : 'Card payment is unavailable. Please try again later.');
      setConfirmOpen(false);
    } finally { setSubmitting(false); }
  };

  const completeMockPayment = async () => {
    if (!intent?.mock_mode || mockCompleting) return;
    setMockCompleting(true); setIntentError('');
    try {
      await publicInvoicePaymentService.completeMockPayment(token, intent.intent_id);
      setIntent(null);
      await refresh(false);
    } catch (reason) {
      setIntentError(reason instanceof Error ? reason.message : 'Mock payment could not be completed.');
      await refresh(false);
    } finally { setMockCompleting(false); }
  };

  return <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh', p: { xs: 2, sm: 4 } }} data-testid="public-invoice-payment">
    <Box sx={{ maxWidth: 780, mx: 'auto' }}>
      <img src="/madebb/logo.png" alt="Made.bb" style={{ width: 112, height: 70, objectFit: 'contain', objectPosition: 'left' }} />
      {loading && <Stack role="status" direction="row" spacing={1} alignItems="center"><CircularProgress size={20} /><span>Loading your invoice…</span></Stack>}
      {!loading && loadError && <Alert severity="error" role="alert" action={tokenPattern.test(token) ? <Button color="inherit" onClick={() => { void refresh(true); }}>Retry</Button> : undefined}>{loadError} Please contact {contact} for help.</Alert>}
      {!loading && invoice && <Paper variant="outlined" sx={{ p: { xs: 2, sm: 4 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h5" fontWeight={700}>{invoice.seller.name}</Typography>
            {invoice.seller.address && <Typography variant="body2">{invoice.seller.address}</Typography>}
            {invoice.seller.tin && <Typography variant="body2">TIN {invoice.seller.tin}</Typography>}
            <Typography variant="body2">{invoice.seller.email || 'info@made.bb'}</Typography>
          </Box>
          <Box><Typography variant="h5" fontWeight={700}>Invoice {invoice.number}</Typography>
            <Typography variant="body2">Issued {invoice.issued_at?.slice(0, 10)}</Typography>
            {invoice.due_date && <Typography variant="body2">Due {invoice.due_date.slice(0, 10)}</Typography>}
          </Box>
        </Stack>
        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle2">Bill to</Typography>
        <Typography>{invoice.customer.company || invoice.customer.name}</Typography>
        {invoice.customer.company && <Typography variant="body2">{invoice.customer.name}</Typography>}
        {invoice.customer.billing_address && <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{invoice.customer.billing_address}</Typography>}
        <Box sx={{ overflowX: 'auto', mt: 3 }}><table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 420 }} aria-label="Invoice items">
          <thead><tr><th scope="col" style={{ textAlign: 'left' }}>Item</th><th scope="col">Qty</th><th scope="col">Unit price</th><th scope="col">Line amount</th></tr></thead>
          <tbody>{invoice.lines.map((line, index) => <tr key={`${index}-${line.description}`}><td style={{ padding: '8px 0', borderTop: '1px solid #ddd' }}>{line.description}</td><td style={{ textAlign: 'right' }}>{line.quantity}</td><td style={{ textAlign: 'right' }}>{money(line.unit_price)}</td><td style={{ textAlign: 'right' }}>{money(line.subtotal)}</td></tr>)}</tbody>
        </table></Box>
        <Stack spacing={0.5} sx={{ maxWidth: 330, ml: 'auto', mt: 3 }}>
          <Stack direction="row" justifyContent="space-between"><span>Subtotal</span><strong>{money(invoice.subtotal)}</strong></Stack>
          <Stack direction="row" justifyContent="space-between"><span>Invoice discount</span><strong>− {money(invoice.invoice_discount)}</strong></Stack>
          <Stack direction="row" justifyContent="space-between"><span>VAT</span><strong>{money(invoice.vat)}</strong></Stack>
          {invoice.adjustments.map((adjustment, index) => <Stack direction="row" justifyContent="space-between" key={index}><span>{adjustment.kind.replaceAll('_', ' ')}</span><strong>{money(adjustment.total_delta)}</strong></Stack>)}
          <Divider />
          <Stack direction="row" justifyContent="space-between"><Typography fontWeight={700}>Invoice total</Typography><Typography fontWeight={700}>{money(invoice.total)}</Typography></Stack>
          <Stack direction="row" justifyContent="space-between"><span>Received</span><strong>{money(invoice.paid)}</strong></Stack>
          <Stack direction="row" justifyContent="space-between"><span>Balance</span><strong>{money(invoice.balance)}</strong></Stack>
        </Stack>
        <Divider sx={{ my: 3 }} />
        {invoice.status === 'paid' && <Alert severity="success" role="status">Paid. Thank you. Your payment has been verified.</Alert>}
        {invoice.status === 'waiting_for_completion' && <Alert severity="info" role="status">Your deposit has been received. The balance will become payable after your order is complete.</Alert>}
        {invoice.status === 'finance_review' && <Alert severity="warning" role="status">This invoice is under payment review. Card payment is unavailable; please contact {contact}.</Alert>}
        {invoice.status === 'voided' && <Alert severity="warning" role="status">This invoice has been voided. Card payment is unavailable; please contact {contact}.</Alert>}
        {invoice.status === 'payment_due' && <Stack spacing={2}>
          {returned && !failedReturn && <Alert severity="info" role="status">We are waiting for payment verification. Returning from the card provider does not confirm payment. Refresh this page for the latest status.</Alert>}
          {failedReturn && <Alert severity="warning" role="status">Card payment was not confirmed. If you were charged, please contact {contact} before trying again.</Alert>}
          <Typography variant="h6">Amount payable now: {money(invoice.amount_due_now)}</Typography>
          {invoice.amount_due_now !== invoice.balance && <Typography variant="body2">The amount payable now differs from the outstanding balance under this invoice’s payment terms.</Typography>}
          {intentError && <Alert severity="error" role="alert">{intentError}</Alert>}
          {intent && <Alert severity="info" role="status">{intent.mock_mode
            ? `Staging mock payment is ready for ${money(intent.amount)} (${intent.installment}). No card will be charged.`
            : `A secure card payment link is ready for ${money(intent.amount)} (${intent.installment}). This is not a payment confirmation.`}</Alert>}
          {intent?.mock_mode && <Button variant="contained" disabled={mockCompleting} onClick={() => { void completeMockPayment(); }} sx={{ alignSelf: 'flex-start' }}>{mockCompleting ? 'Recording test payment…' : 'Complete mock payment'}</Button>}
          {intent && !intent.mock_mode && <Button component="a" href={safeProviderUrl(intent) || undefined} referrerPolicy="no-referrer" variant="contained" sx={{ alignSelf: 'flex-start' }}>Continue to secure card payment</Button>}
          {!intent && !returned && invoice.can_pay && <Button variant="contained" sx={{ alignSelf: 'flex-start' }} onClick={() => setConfirmOpen(true)}>{invoice.mock_mode ? 'Test payment' : 'Pay now'}</Button>}
          <Button variant="text" sx={{ alignSelf: 'flex-start' }} disabled={refreshing} onClick={() => { void refresh(false); }}>{refreshing ? 'Checking…' : 'Refresh payment status'}</Button>
        </Stack>}
        <Typography variant="body2" color="text.secondary" mt={3}>Questions about this invoice? Contact {contact}.</Typography>
      </Paper>}
      <Dialog open={confirmOpen} onClose={() => { if (!submitting) setConfirmOpen(false); }} aria-labelledby="invoice-payment-confirm-title">
        <DialogTitle id="invoice-payment-confirm-title">{invoice?.mock_mode ? 'Confirm staging test payment' : 'Confirm card payment'}</DialogTitle>
        <DialogContent><Typography>{invoice?.mock_mode
          ? 'This staging test will record a simulated payment. No card will be charged. '
          : 'You will request a secure card payment link for '}
          <strong>{invoice && money(invoice.amount_due_now)}</strong>. The amount is set by the invoice and cannot be changed here.</Typography></DialogContent>
        <DialogActions><Button onClick={() => setConfirmOpen(false)} disabled={submitting}>Cancel</Button><Button variant="contained" onClick={createIntent} disabled={submitting}>{submitting ? 'Preparing…' : 'Confirm amount'}</Button></DialogActions>
      </Dialog>
    </Box>
  </Box>;
};
